import { ScimEntityService } from './ScimEntityService';
import { debugLog, debugVerboseLog } from '../utils/debugLog';

export interface ContentType {
    Id: string;
    Ext: string;
    ContentType: string;
    Version: string;
}

class ContentTypesCacheService {
    private static instance: ContentTypesCacheService;
    private cache: {
        contentTypes: ContentType[] | null;
        lastFetch: number | null;
        isLoading: boolean;
        error: string | null;
    } = {
        contentTypes: null,
        lastFetch: null,
        isLoading: false,
        error: null
    };

    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private entityService: ScimEntityService;

    private constructor() {
        this.entityService = new ScimEntityService();
    }

    public static getInstance(): ContentTypesCacheService {
        if (!ContentTypesCacheService.instance) {
            ContentTypesCacheService.instance = new ContentTypesCacheService();
        }
        return ContentTypesCacheService.instance;
    }

    /**
     * Get cached content types, fetching from API if needed
     */
    public async getContentTypes(): Promise<ContentType[]> {
        const now = Date.now();

        // Return cached data if it's still valid
        if (this.cache.contentTypes &&
            this.cache.lastFetch &&
            (now - this.cache.lastFetch) < this.CACHE_DURATION) {
            debugVerboseLog('📋 ContentTypesCacheService: Returning cached content types');
            return this.cache.contentTypes;
        }

        // If already loading, wait for the current request
        if (this.cache.isLoading) {
            debugVerboseLog('📋 ContentTypesCacheService: Waiting for existing content types fetch...');
            return new Promise((resolve, reject) => {
                const checkCache = () => {
                    if (!this.cache.isLoading) {
                        if (this.cache.error) {
                            reject(new Error(this.cache.error));
                        } else {
                            resolve(this.cache.contentTypes || []);
                        }
                    } else {
                        setTimeout(checkCache, 100);
                    }
                };
                checkCache();
            });
        }

        // Fetch fresh data
        this.cache.isLoading = true;
        this.cache.error = null;

        try {
            debugLog('📋 ContentTypesCacheService: Fetching fresh content types from API...');
            const contentTypes = await this.entityService.getContentTypes();

            this.cache.contentTypes = contentTypes;
            this.cache.lastFetch = now;
            this.cache.error = null;

            debugLog(`📋 ContentTypesCacheService: Cached ${contentTypes.length} content types`);
            return contentTypes;
        } catch (error) {
            console.error('❌ ContentTypesCacheService: Error fetching content types:', error);
            this.cache.error = error instanceof Error ? error.message : 'Failed to load content types';
            throw error;
        } finally {
            this.cache.isLoading = false;
        }
    }

    /**
     * Get content type by MIME type
     */
    public async getContentTypeByMimeType(mimeType: string): Promise<ContentType | undefined> {
        const contentTypes = await this.getContentTypes();
        return contentTypes.find(ct => 
            ct.ContentType.toLowerCase() === mimeType.toLowerCase()
        );
    }

    /**
     * Get content types by file extension
     */
    public async getContentTypesByExtension(ext: string): Promise<ContentType[]> {
        const contentTypes = await this.getContentTypes();
        const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;
        return contentTypes.filter(ct => 
            ct.Ext.toLowerCase() === normalizedExt.toLowerCase()
        );
    }

    /**
     * Clear the cache to force a fresh fetch
     */
    public clearCache(): void {
        debugLog('🗑️ ContentTypesCacheService: Clearing content types cache');
        this.cache.contentTypes = null;
        this.cache.lastFetch = null;
        this.cache.error = null;
    }

    /**
     * Get cache status
     */
    public getCacheStatus(): {
        hasData: boolean;
        isLoading: boolean;
        error: string | null;
        lastFetch: number | null;
    } {
        return {
            hasData: this.cache.contentTypes !== null,
            isLoading: this.cache.isLoading,
            error: this.cache.error,
            lastFetch: this.cache.lastFetch
        };
    }
}

export default ContentTypesCacheService;
